import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxOption, OnyxPhone } from '@onyx/angular';
import {
  ActionHelper,
  FormHelper,
  ONYX_INPUT_DEBOUNCE_TIME,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxFormMode,
  OnyxPhoneNumberInputComponent,
  onyxPhoneValidator,
  OnyxTabsComponent,
  OnyxTextareaComponent,
  onyxTextareaLengthValidator,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  OnyxToastService,
} from '@onyx/angular';
import { round } from 'lodash';
import { DateTime } from 'luxon';
import type { Observable, Subject } from 'rxjs';
import { debounceTime, map, of, ReplaySubject, switchMap, tap } from 'rxjs';
import type { Merge } from 'type-fest';
import { NoteFormComponent } from '../../../../../../../common/components/forms/note-form/note-form.component';
import { YES_NO_OPTIONS } from '../../../../../../../common/constants/common/yes-no-options';
import { DictionaryCode } from '../../../../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../../../../common/helpers/common.helper';
import { ValidationHelper } from '../../../../../../../common/helpers/validation.helper';
import { AmountService } from '../../../../../../../common/services/amount.service';
import { DictionariesService } from '../../../../../../../common/services/dictionaries.service';
import { PreferencesService } from '../../../../../../../common/services/preferences.service';
import { ContractorsRoute } from '../../../../../../management-panel/contractors/common/enums/contractors-route';
import { ContractorHelper } from '../../../../../../management-panel/contractors/common/helpers/contractor.helper';
import type { Contractor } from '../../../../../../management-panel/contractors/common/interfaces/contractor';
import type { ContractorContactPerson } from '../../../../../../management-panel/contractors/common/interfaces/contractor-contact-person';
import { ContractorsService } from '../../../../../../management-panel/contractors/common/services/contractors.service';
import { OrderRateType } from '../../../../../common/enums/order-rate-type';
import { OrderHelper } from '../../../../../common/helpers/order.helper';
import type { Order } from '../../../../../common/interfaces/order';
import { OrdersService } from '../../../../../common/services/orders.service';

type OrderModalOutsourcingFormGroup = ReturnType<
  OrderModalOutsourcingFormComponent['buildForm']
>;

export type OrderModalOutsourcingForm = ReturnType<
  OrderModalOutsourcingFormGroup['getRawValue']
>;

export type OrderModalOutsourcingFormDto = Merge<
  OrderModalOutsourcingForm,
  { secondaryDriver: OrderModalOutsourcingForm['secondaryDriver'] | null }
>;

@Component({
  selector: 'app-order-modal-outsourcing-form',
  imports: [
    OnyxDropdownComponent,
    OnyxTextFieldComponent,
    OnyxPhoneNumberInputComponent,
    OnyxTabsComponent,
    OnyxTextFieldComponent,
    OnyxButtonComponent,
    OnyxFormGroupComponent,
    ReactiveFormsModule,
    TranslatePipe,
    AsyncPipe,
    OnyxTextareaComponent,
    AsyncPipe,
  ],
  templateUrl: './order-modal-outsourcing-form.component.html',
  styleUrl: './order-modal-outsourcing-form.component.scss',
})
export class OrderModalOutsourcingFormComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly preferencesService = inject(PreferencesService);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly amountService = inject(AmountService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ordersService = inject(OrdersService);
  private readonly contractorHelper = inject(ContractorHelper);
  private readonly contractorsService = inject(ContractorsService);
  private readonly orderHelper = inject(OrderHelper);

  public readonly order = input.required<Order>();
  public readonly mode = input.required<OnyxFormMode>();
  public readonly close$ = input.required<Subject<void>>();

  public readonly cancelEdit = output<void>();

  protected readonly I18N = 'orders.orderModal.outsourcingForm';
  protected readonly CONTRACTORS_SOURCE =
    this.contractorHelper.getContractorsSource();
  protected readonly YES_NO_OPTIONS = YES_NO_OPTIONS;

  protected readonly OnyxFormMode = OnyxFormMode;
  protected readonly DictionaryCode = DictionaryCode;

  protected readonly currencies$ = this.dictionariesService.getDictionary(
    DictionaryCode.CURRENCY,
  );
  protected readonly vatRates$ = this.dictionariesService.getDictionary(
    DictionaryCode.VAT_RATE,
  );

  protected orderPrice = computed(() => this.order().price);

  protected form = this.buildForm();
  protected loading = signal(false);
  protected hasDoubleCrew = signal(false);
  protected contractor$ = new ReplaySubject<Contractor | null>(1);
  protected contractorBranches$!: Observable<OnyxOption<string>[] | null>;
  protected contractorContactPersons$!: Observable<
    OnyxOption<ContractorContactPerson>[] | null
  >;
  protected contractorLink$ = this.contractor$.pipe(
    map((contractor) =>
      CommonHelper.getPreviewLink(contractor, (contractor) =>
        this.contractorHelper.openModal(contractor),
      ),
    ),
  );

  protected currencyControl = this.form.controls.rate.controls.currency;
  protected currency = toSignal(this.currencyControl.valueChanges, {
    initialValue: this.currencyControl.getRawValue(),
  });

  constructor() {
    effect(() => {
      ValidationHelper.toggleControls(this.form.controls.secondaryDriver, {
        enabled: this.hasDoubleCrew(),
        reset: true,
      });
    });

    this.form.controls.rate.valueChanges
      .pipe(
        debounceTime(ONYX_INPUT_DEBOUNCE_TIME),
        switchMap((amount) => {
          if (!amount.value && !amount.currency) return of(null);
          if (amount.currency === this.orderPrice().currency) {
            return of(this.orderPrice().value);
          }

          return this.amountService
            .calculateAmount({
              date: DateTime.now().toISODate(),
              value: this.orderPrice().value,
              currency: this.orderPrice().currency,
              resultCurrency: amount.currency!,
            })
            .pipe(map((orderPrice) => orderPrice.resultCurrencyValue));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((orderPrice) => {
        const controls = this.form.controls;
        const rateValue = controls.rate.getRawValue()?.value;
        if (!orderPrice || !rateValue) return;

        const marginRate = orderPrice - rateValue;
        const marginPercentage = round(
          (marginRate / orderPrice) * 10 ** (2 * 2),
          2,
        );

        controls.marginPercentage.setValue(marginPercentage);
        controls.marginRate.patchValue({
          value: marginRate,
          currency: controls.rate.getRawValue().currency,
        });
      });
  }

  public ngOnInit(): void {
    const outsourcing = this.order().outsourcingData;
    if (outsourcing && this.mode() === OnyxFormMode.EDIT) {
      this.form.patchValue({
        ...outsourcing,
        contractor: outsourcing.contractor.uuid,
        contractorBranch: outsourcing.contractorBranch?.uuid ?? null,
        secondaryDriver: outsourcing.secondaryDriver
          ? {
              name: outsourcing.secondaryDriver.name,
              phone: outsourcing.secondaryDriver.phone,
            }
          : undefined,
        note: { content: outsourcing.note?.content },
      });
    }

    const controls = this.form.controls;
    const { uuid, registrationNumber } =
      this.order().basicInformation.loadedSemiTrailer ?? {};

    if (uuid || registrationNumber) {
      controls.trailerRegistrationNumber.disable();
    }

    this.contractorBranches$ = this.contractor$.pipe(
      switchMap((contractor) =>
        contractor ? this.contractorsService.getBranches(contractor) : of(null),
      ),
      tap((branches) => {
        ValidationHelper.toggleControls(controls.contractorBranch, {
          enabled: branches != null,
          reset: false,
        });

        if (branches && !controls.contractorBranch.value) {
          setTimeout(() =>
            controls.contractorBranch.setValue(branches[0].value),
          );
        }
      }),
    );

    this.contractorContactPersons$ = this.contractor$.pipe(
      map((contractor) =>
        contractor
          ? contractor.contactPersons.map((contactPerson) => ({
              name: `${contactPerson.firstName} ${contactPerson.lastName}`,
              value: contactPerson,
            }))
          : null,
      ),
      tap((contactPersons) => {
        ValidationHelper.toggleControls(controls.contactPerson, {
          enabled: !!contactPersons?.length,
          reset: true,
        });
        setTimeout(() =>
          controls.contactPerson.setValue(contactPersons?.[0]?.value ?? null),
        );
      }),
    );
  }

  protected deleteOutsourcing(): void {
    const order = this.order();
    if (order) this.orderHelper.deleteOutsourcing(order);
  }

  protected addContractor(): void {
    ActionHelper.openLink(ContractorsRoute.ADD_CONTRACTOR);
  }

  protected editContractor(): void {
    const contractorUuid = this.form.controls.contractor.getRawValue();
    if (!contractorUuid) return;

    ActionHelper.openLink(
      ContractorsRoute.EDIT_CONTRACTOR.replace(':uuid', contractorUuid),
    );
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();
    const dto: OrderModalOutsourcingFormDto = {
      ...form,
      secondaryDriver: this.hasDoubleCrew() ? form.secondaryDriver : null,
    };

    const action$ = {
      [OnyxFormMode.ADD]: this.ordersService.addOutsourcing(
        this.order().uuid,
        dto,
      ),
      [OnyxFormMode.EDIT]: this.ordersService.editOutsourcing(
        this.order().uuid,
        dto,
      ),
    }[this.mode()];

    this.loading.set(true);
    action$
      .subscribe(() => {
        const message = {
          [OnyxFormMode.ADD]: `orders.orderModal.toasts.outsourcingReady`,
          [OnyxFormMode.EDIT]: `labels.savedChanges`,
        }[this.mode()];

        this.toastService.showSuccess({ message });
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    const buildDriverForm = (options?: { disabled: boolean }) => {
      const group = this.fb.group({
        name: this.fb.control<string | null>(null, [
          Validators.required,
          onyxTextFieldLengthValidator,
        ]),
        phone: this.fb.control<OnyxPhone | null>(null, [
          Validators.required,
          onyxPhoneValidator,
        ]),
      });
      if (options?.disabled) group.disable();
      return group;
    };

    return this.fb.group({
      contractor: this.fb.control<string | null>(null, [Validators.required]),
      contractorBranch: this.fb.control<string | null>(null, [
        Validators.required,
      ]),
      contactPerson: this.fb.control<ContractorContactPerson | null>(
        { value: null, disabled: true },
        [Validators.required],
      ),
      vehicleRegistrationNumber: this.fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      trailerRegistrationNumber: this.fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
      primaryDriver: buildDriverForm(),
      secondaryDriver: buildDriverForm({ disabled: true }),
      rateType: this.fb.control<OrderRateType | null>(OrderRateType.FREIGHT, [
        Validators.required,
      ]),
      rate: FormHelper.buildAmountForm(this.fb, this.preferencesService, {
        minValue: 0,
      }),
      vatRate: this.fb.control<string | null>(null, [Validators.required]),
      marginPercentage: this.fb.control<number | null>(
        { value: null, disabled: true },
        [Validators.required],
      ),
      marginRate: FormHelper.buildAmountForm(this.fb, this.preferencesService, {
        disabled: true,
      }),
      executionTerms: this.fb.control<string | null>(null, [
        onyxTextareaLengthValidator,
      ]),
      note: NoteFormComponent.buildForm(this.fb),
    });
  }
}
